import { useState } from 'react';
import { COVER_PRESETS } from '@/services/albumService';

interface AlbumFormProps {
  initialTitle?: string;
  initialDescription?: string;
  initialCoverImage?: string;
  onSubmit: (data: { title: string; description: string; coverImage: string }) => void | Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}

export default function AlbumForm({
  initialTitle = '',
  initialDescription = '',
  initialCoverImage = COVER_PRESETS[0].image,
  onSubmit,
  onCancel,
  submitLabel,
}: AlbumFormProps) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [coverImage, setCoverImage] = useState(initialCoverImage);
  const [titleError, setTitleError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError('앨범 이름을 입력해주세요');
      return;
    }
    setTitleError('');
    setSubmitError('');
    setSubmitting(true);
    try {
      await onSubmit({ title: trimmedTitle, description: description.trim(), coverImage });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : '앨범을 저장하지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* Cover Image Preview */}
      <div>
        <label className="block text-sm font-medium text-foreground-800 mb-2">
          대표 사진
        </label>
        <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-secondary-100 mb-3">
          <img
            src={coverImage}
            alt="대표 사진 미리보기"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {COVER_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => setCoverImage(preset.image)}
              className={`relative aspect-[4/3] rounded-lg overflow-hidden bg-secondary-100 cursor-pointer transition-all duration-200 ${
                coverImage === preset.image
                  ? 'ring-2 ring-accent-500 ring-offset-1 scale-95'
                  : 'hover:opacity-80'
              }`}
            >
              <img
                src={preset.image}
                alt="커버 옵션"
                className="w-full h-full object-cover"
              />
              {coverImage === preset.image && (
                <div className="absolute inset-0 bg-accent-500/20 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center">
                    <i className="ri-check-line text-background-50 text-sm"></i>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Album Name */}
      <div>
        <label
          htmlFor="album-title"
          className="block text-sm font-medium text-foreground-800 mb-1.5"
        >
          앨범 이름 <span className="text-red-400">*</span>
        </label>
        <input
          id="album-title"
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (titleError) setTitleError('');
          }}
          placeholder="예: 2026 여름 가족 여행"
          maxLength={50}
          className={`w-full px-4 py-3 rounded-xl border text-sm bg-background-50 text-foreground-900 placeholder:text-foreground-400 transition-colors outline-none ${
            titleError
              ? 'border-red-300 focus:border-red-400'
              : 'border-background-200/70 focus:border-accent-500'
          }`}
        />
        {titleError && (
          <p className="mt-1 text-xs text-red-400">{titleError}</p>
        )}
        <p className="mt-1 text-xs text-foreground-400">
          {title.length}/50자
        </p>
      </div>

      {/* Album Description */}
      <div>
        <label
          htmlFor="album-desc"
          className="block text-sm font-medium text-foreground-800 mb-1.5"
        >
          앨범 설명
        </label>
        <textarea
          id="album-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="앨범에 대한 간단한 설명을 적어보세요"
          maxLength={200}
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-background-200/70 text-sm bg-background-50 text-foreground-900 placeholder:text-foreground-400 transition-colors outline-none focus:border-accent-500 resize-none"
        />
        <p className="mt-1 text-xs text-foreground-400">
          {description.length}/200자
        </p>
      </div>

      {submitError && (
        <p className="text-sm text-red-500">{submitError}</p>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 px-4 py-3 rounded-xl border border-background-200/70 text-sm font-medium text-foreground-600 bg-background-50 hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          취소
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-background-50 bg-accent-500 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          {submitting ? '저장 중...' : submitLabel}
        </button>
      </div>
    </form>
  );
}